import React, { useState, useImperativeHandle, forwardRef, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import AppContext from '../../contexts/AppContext';
import Label from '../Label';
import {
  CardContainer,
  TwoColumnLayout,
  SectionTitle as SharedSectionTitle,
  FormContent,
  StepContainer,
  Section,
  Input,
  Textarea,
  Divider,
} from '../PrebookingSharedComponents';
import LanguageSelection from './LanguageSelection';
import FileUpload from './FileUpload';
import DeliveryDatePicker from './DeliveryDatePicker';
import { ErrorText } from './shared';
import PreferenceGroup from '../PreferenceGroup';

import i18n from '../../i18n';
import { validateStep1 } from '../../utils/translationOrderValidation';

const SectionTitle = styled(SharedSectionTitle)`
  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const TranslationOrderDetailsStep = forwardRef(({
  formData,
  updateField,
  addAttachment,
  removeAttachment,
  updateAttachmentScope,
}, ref) => {
  const { user } = useContext(AppContext);
  const [errors, setErrors] = useState({});

  const externalOrderIdUsed = user?.userGroup?.externalOrderIdUsed;
  const externalOrderIdMandatory = user?.userGroup?.externalOrderIdMandatory;
  const externalOrderIdTitle = user?.userGroup?.externalOrderIdTitle || 'External order id';

  useImperativeHandle(ref, () => ({
    validate: () => {
      const validationErrors = validateStep1(formData, {
        externalOrderIdUsed,
        externalOrderIdMandatory,
      });
      setErrors(validationErrors);
      return Object.keys(validationErrors).length === 0;
    },
  }), [formData, externalOrderIdUsed, externalOrderIdMandatory]);

  const clearError = (field) => {
    setErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
  };

  return (
    <StepContainer>
      <CardContainer>
        <TwoColumnLayout>
          <SectionTitle>{i18n('translation_order_details')}</SectionTitle>
          <FormContent>
            <LanguageSelection
              formData={formData}
              updateField={updateField}
              errors={errors}
              clearError={clearError}
            />

            <FileUpload
              attachments={formData.attachments}
              errors={errors}
              addAttachment={addAttachment}
              removeAttachment={removeAttachment}
              updateAttachmentScope={updateAttachmentScope}
              setErrors={setErrors}
            />
          </FormContent>
        </TwoColumnLayout>

        <Divider style={{ margin: '24px 0' }} />

        <TwoColumnLayout>
          <SectionTitle>{i18n('preferences')}</SectionTitle>
          <FormContent>
            {formData.translationLevels?.length > 0 && (
              <>
                <PreferenceGroup
                  id="translatorType"
                  label={i18n('translation_order_translator_type')}
                  options={formData.translationLevels.map((level) => ({
                    value: level.id,
                    label: level.name,
                  }))}
                  selectedValues={formData.translatorLevelIds || []}
                  multiSelect
                  onChange={(values) => {
                    updateField('translatorLevelIds', values);
                    clearError('translatorLevelIds');
                  }}
                  error={errors.translatorLevelIds && i18n(errors.translatorLevelIds)}
                />

                <Divider />
              </>
            )}

            <Section>
              <Label text={i18n('translation_order_instructions')} />
              <Textarea
                value={formData.instructionsForTranslator}
                onChange={(e) => updateField('instructionsForTranslator', e.target.value)}
                placeholder={i18n('translation_order_instructions_placeholder')}
                rows={4}
              />
            </Section>

            <Divider />

            <DeliveryDatePicker
              selected={formData.desiredDeliveryDate}
              onChange={(date) => {
                updateField('desiredDeliveryDate', date);
                clearError('desiredDeliveryDate');
              }}
              error={errors.desiredDeliveryDate}
            />

            {externalOrderIdUsed && (
              <>
                <Divider />
                <Section>
                  <Label text={externalOrderIdTitle} required={!!externalOrderIdMandatory} />
                  <Input
                    type="text"
                    value={formData.externalOrderId || ''}
                    onChange={(e) => {
                      updateField('externalOrderId', e.target.value);
                      if (e.target.value.trim()) {
                        clearError('externalOrderId');
                      }
                    }}
                    placeholder={externalOrderIdTitle}
                  />
                  {errors.externalOrderId && <ErrorText>{i18n(errors.externalOrderId)}</ErrorText>}
                </Section>
              </>
            )}
          </FormContent>
        </TwoColumnLayout>
      </CardContainer>
    </StepContainer>
  );
});

TranslationOrderDetailsStep.displayName = 'TranslationOrderDetailsStep';

TranslationOrderDetailsStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  addAttachment: PropTypes.func.isRequired,
  removeAttachment: PropTypes.func.isRequired,
  updateAttachmentScope: PropTypes.func.isRequired,
};

export default TranslationOrderDetailsStep;
